import PropTypes from "prop-types";
import React from "react";

import "./resumoPartida.css";

/**
 * O resumo que o aluno vê ao terminar uma partida, sobre o iframe do conteúdo.
 *
 * Aparece a cada `JOGO_CONCLUIDO`. Não fecha sozinho: sai no × ou no Continuar.
 *
 * Cada linha só é renderizada quando o dado existe. Jogo de labirinto não tem
 * pergunta e nem todo jogo manda fase, então "Nível 2" e "respondeu X de Y"
 * somem em vez de exibir zero ou `null` — inventar número aqui daria ao aluno
 * uma informação falsa sobre a própria partida.
 *
 * Dois "níveis" convivem e NÃO são a mesma coisa:
 *   - `nivel`      -> a fase do jogo, veio no postMessage;
 *   - `xp.level`   -> o nível de XP do aluno, veio da resposta do servidor.
 * Por isso o segundo só aparece dentro do bloco de XP, nunca solto.
 *
 * O bloco de XP depende de `xp_awarded > 0`, que o servidor só calcula quando o
 * módulo de gamificação está ativo para o usuário. A regra vive lá, não aqui:
 * este componente apenas mostra o que foi creditado.
 */
const ResumoPartida = ({
  titulo,
  pagina,
  ehJogo,
  temGamificacao,
  nivel,
  acertos,
  erros,
  totalPerguntas,
  xp,
  onFechar
}) => {
  const respondidas = acertos + erros;

  // Aproveitamento sobre o que o aluno respondeu, não sobre o total do jogo:
  // quem saiu na metade não deve aparecer com 40% por causa das que não viu.
  const aproveitamento =
    respondidas > 0 ? Math.round((acertos / respondidas) * 100) : null;

  const ganhouXp = xp && Number(xp.xp_awarded) > 0;

  /* Ja ganhou hoje: o servidor respondeu, o aluno TEM o modulo e ainda assim nao
     houve credito — a regra e "uma vez por dia por LO". Sem esta mensagem a linha
     de XP simplesmente desaparecia na segunda partida, e a crianca nao entendia
     por que. `temGamificacao` evita dizer isso a quem nunca ganharia. */
  const jaGanhouHoje = !!xp && !ganhouXp && temGamificacao;

  return (
    <div className="resumo-partida-veu">
      <div
        className="resumo-partida"
        role="dialog"
        aria-modal="true"
        aria-labelledby="resumo-partida-titulo"
      >
        <button
          type="button"
          className="resumo-partida-fechar"
          onClick={onFechar}
          aria-label="Fechar"
        >
          &times;
        </button>

        <div className="resumo-partida-faixa">Seu resultado</div>

        <div className="resumo-partida-corpo">
          {/* Nome do conteúdo, não do game_key: é o que o aluno vê no topo da
              tela e reconhece. Quebra em duas linhas em vez de truncar — nome de
              conteúdo cortado com "..." não identifica nada. */}
          <h3 className="resumo-partida-titulo" id="resumo-partida-titulo">
            {titulo}
          </h3>

          {/* Nome da pagina jogada, quando a aula tem mais de uma. Vem do React
              (`children[page - 1]`), nao do postMessage. */}
          {pagina && <p className="resumo-partida-pagina">{pagina}</p>}

          {nivel !== null && nivel !== undefined && (
            <p className="resumo-partida-fase">Nível {nivel}</p>
          )}

          <div className="resumo-partida-placar">
            <div className="resumo-partida-caixa resumo-partida-caixa-acertos">
              <strong>{acertos}</strong>
              <span>{acertos === 1 ? "acerto" : "acertos"}</span>
            </div>
            <div className="resumo-partida-caixa resumo-partida-caixa-erros">
              <strong>{erros}</strong>
              <span>{erros === 1 ? "erro" : "erros"}</span>
            </div>
          </div>

          {totalPerguntas !== null && totalPerguntas !== undefined && (
            <p className="resumo-partida-respondidas">
              Você acertou {acertos} de {totalPerguntas}
            </p>
          )}

          {aproveitamento !== null && (
            <div className="resumo-partida-barra-linha">
              <div className="resumo-partida-barra">
                <div
                  className="resumo-partida-barra-preenchida"
                  style={{ width: `${aproveitamento}%` }}
                />
              </div>
              <span className="resumo-partida-porcentagem">
                {aproveitamento}%
              </span>
            </div>
          )}

          {(ganhouXp || jaGanhouHoje) && (
            <div className="resumo-partida-xp">
              {ganhouXp ? (
                <p className="resumo-partida-xp-ganho">+{xp.xp_awarded} XP</p>
              ) : (
                <p className="resumo-partida-xp-repetido">
                  Você já ganhou XP {ehJogo ? "neste jogo" : "nesta aula"} hoje.
                </p>
              )}
            </div>
          )}

          <button
            type="button"
            className="resumo-partida-continuar"
            onClick={onFechar}
          >
            Continuar
          </button>
        </div>
      </div>
    </div>
  );
};

ResumoPartida.propTypes = {
  titulo: PropTypes.string,
  pagina: PropTypes.string,
  ehJogo: PropTypes.bool,
  temGamificacao: PropTypes.bool,
  nivel: PropTypes.number,
  acertos: PropTypes.number,
  erros: PropTypes.number,
  totalPerguntas: PropTypes.number,
  /* Só `xp_awarded` é lido. O servidor manda `xp`, `level` e
     `xp_para_proximo_nivel` junto, e PropTypes.shape aceita chaves extras. */
  xp: PropTypes.shape({
    xp_awarded: PropTypes.number
  }),
  onFechar: PropTypes.func.isRequired
};

ResumoPartida.defaultProps = {
  titulo: "Jogo concluído",
  pagina: null,
  ehJogo: false,
  temGamificacao: false,
  nivel: null,
  acertos: 0,
  erros: 0,
  totalPerguntas: null,
  xp: null
};

export default ResumoPartida;
